import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Button } from "@landtechnologies/components";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import classNames from "classnames";
import {
  LetterEventName,
  logLetterEvent,
  mapToLetterFormStepStringEnum,
} from "../helpers/logLetterEvent";
import { Stores, useGlobalState } from "react-migration/lib";
import { clearStore, updateLetterName } from "src/js/stores/letter/actions";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";

export const Header = () => {
  const { t } = useTranslation();
  const [{ currentStep, letterName }] = useGlobalState(Stores.letter);
  const [{ siteCardId }] = useGlobalState(Stores.site);
  const [isTitleEditOn, setIsTitleEdit] = useState(false);
  const [letterTitle, setletterTitle] = useState(letterName || t("sites.letters.untitled_letter"));

  const inputRef = useRef<HTMLInputElement>(null);
  const helperDivRef = useRef<HTMLDivElement>(null);

  const changeTitleEdit = () => {
    updateLetterName(letterTitle || t("sites.letters.untitled_letter"));
    if (isTitleEditOn) {
      logLetterEvent({
        name: LetterEventName.LETTERS_FORM_LETTER_TITLE_UPDATED,
        context: { step: mapToLetterFormStepStringEnum(currentStep) },
      });
    }
    if (!letterTitle) {
      setletterTitle(t("sites.letters.untitled_letter"));
    }
    setIsTitleEdit(!isTitleEditOn);
  };

  const onCloseClick = () => {
    clearStore();
    router.push({
      name: ROUTES.SITES,
      query: {
        card_page: "letters",
        site_id: siteCardId,
      },
    });
  };

  const onLeftButtonClick = () => {
    onCloseClick();
    logLetterEvent({
      name: LetterEventName.LETTERS_FORM_BACK_TO_SITES_CLICKED,
      context: { step: mapToLetterFormStepStringEnum(currentStep) },
    });
  };

  const onRightButtonClick = () => {
    onCloseClick();
    logLetterEvent({
      name: LetterEventName.LETTERS_FORM_SAVE_AND_CLOSE_CLICKED,
      context: { step: mapToLetterFormStepStringEnum(currentStep) },
    });
  };

  useEffect(() => {
    updateLetterName(letterTitle);
  }, []);

  useEffect(() => {
    if (helperDivRef?.current && inputRef?.current) {
      helperDivRef.current.textContent = letterTitle;
      inputRef.current.style.width = helperDivRef.current.offsetWidth + "px";
    }
  }, [letterTitle, isTitleEditOn]);

  const onTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setletterTitle(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      changeTitleEdit();
    }
  };

  return (
    <div
      data-testid="letters-header"
      className="atlas-w-full atlas-border atlas-py-2 atlas-bg-background-white atlas-px-6 atlas-border-b atlas-border-background-dark atlas-flex atlas-flex-row atlas-justify-between atlas-items-center"
    >
      <Button.Base
        size="large"
        className="-atlas-ml-2 atlas-border-0 atlas-text-content-tertiary"
        onClick={onLeftButtonClick}
        data-testid="letters-header-left-button"
      >
        <i
          className={
            "icon-lt-arrow-left-s-line atlas-w-2 atlas-h-3.5 atlas-mr-3 atlas-fill-content-tertiary"
          }
        />
        {t("sites.letters.back_to_sites_button")}
      </Button.Base>

      {isTitleEditOn ? (
        <>
          <input
            data-testid="letters-header-title-input"
            ref={inputRef}
            autoFocus
            type="text"
            className="atlas-border atlas-border-border-action-focus atlas-px-3 atlas-py-1 atlas-rounded atlas-text-content-tertiary atlas-font-semibold atlas-text-lg atlas-min-w-[150px]"
            required
            value={letterTitle}
            onChange={onTitleChange}
            onBlur={changeTitleEdit}
            onKeyUp={handleKeyDown}
          />
          <div
            ref={helperDivRef}
            className={classNames(
              "atlas-font-semibold",
              "atlas-text-lg",
              "atlas-px-4",
              "atlas-absolute",
              "-atlas-top-96"
            )}
          />
        </>
      ) : (
        <span
          className="atlas-font-semibold atlas-text-lg atlas-text-content-tertiary atlas-cursor-pointer hover:atlas-border hover:atlas-border-border-divider hover:atlas-rounded hover:atlas-px-3 hover:atlas-py-1"
          onClick={changeTitleEdit}
          data-testid="letters-header-title"
        >
          {letterTitle}
        </span>
      )}

      <Button.Secondary data-testid="letters-header-right-button" onClick={onRightButtonClick}>
        {t("sites.letters.save_and_close_button")}
      </Button.Secondary>
    </div>
  );
};
